import React, { useCallback, useEffect, useState } from "react";
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Tooltip,
  TablePagination,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Button,
  Divider,
  Grid,
  FormControlLabel,
  Checkbox,
  Chip,
  Box,
  Autocomplete,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { ButtonBase } from "@mui/material";
import { 
  IconEdit, 
  IconTrash, 
  IconPlus, 
  IconShield, 
  IconShieldOff,
  IconEye,
  IconEyeOff 
} from "@tabler/icons-react";
import MainCard from "ui-component/cards/MainCard";
import SearchSection from "layout/MainLayout/Header/SearchSection";
import useContractPermissions from "hooks/useContractPermissions";

const EditButton = styled(ButtonBase)(({ theme }) => ({
  color: theme.palette.secondary.dark,
  "&:hover": {
    background: theme.palette.secondary.dark,
    color: theme.palette.secondary.light,
  },
  width: 40,
  height: 40,
  borderRadius: 10,
  cursor: "pointer",
}));

const DeleteButton = styled(ButtonBase)(({ theme }) => ({
  color: theme.palette.error.dark,
  "&:hover": {
    background: theme.palette.error.dark,
    color: theme.palette.error.light,
  },
  width: 40,
  height: 40,
  borderRadius: 12,
  cursor: "pointer",
}));

const AddButton = styled(ButtonBase)(({ theme }) => ({
  color: theme.palette.info.dark,
  "&:hover": {
    background: theme.palette.secondary.dark,
    color: theme.palette.secondary.light,
  },
  align: "center",
  gap: 8,
  width: 150,
  height: 40,
  borderRadius: 10,
  display: "flex",
  cursor: "pointer",
}));

const UsersList = () => {
  const { isSuperuser } = useContractPermissions();
  const [users, setUsers] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [page, setPage] = useState(0);
  const [openModal, setOpenModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);
  const [currentUser, setCurrentUser] = useState({
    username: "",
    email: "",
    first_name: "",
    last_name: "",
    password: "",
    password_confirm: "",
    is_active: true,
    profile_id: null,
  });
  const [totalRecords, setTotalRecords] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});

  const fetchUsers = useCallback(() => {
    const searchText = searchTerm !== "" ? `&contains=${searchTerm}` : "";
    fetch(
      `/backend/users/?page=${page + 1}&page_size=${rowsPerPage}${searchText}`
    )
      .then(async (response) => {
        if (!response.ok) {
          let errorMessage = "Não foi possível carregar a lista de usuários. Tente novamente.";
          try {
            const errorData = await response.json();
            if (errorData.error) {
              errorMessage = errorData.error;
            } else if (errorData.detail) {
              errorMessage = errorData.detail;
            }
          } catch (e) {
            console.warn("Não foi possível extrair mensagem de erro específica:", e);
          }
          throw new Error(errorMessage);
        }
        return response.json();
      })
      .then((data) => {
        setUsers(data.results);
        setTotalRecords(data.count);
      })
      .catch((error) => {
        console.error("Erro ao carregar usuários:", error);
        alert(error.message);
      });
  }, [page, rowsPerPage, searchTerm]);

  const fetchProfiles = useCallback(() => {
    fetch("/backend/user-profiles-list/")
      .then(async (response) => {
        if (!response.ok) {
          throw new Error("Não foi possível carregar os perfis.");
        }
        return response.json();
      })
      .then((data) => {
        setProfiles(data);
      })
      .catch((error) => {
        console.error("Erro ao carregar perfis:", error);
      });
  }, []);

  useEffect(() => {
    fetchUsers();
    fetchProfiles();
  }, [fetchUsers, fetchProfiles, page, rowsPerPage, searchTerm]);

  const handleChangePage = (event, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const handleEdit = (user) => {
    setCurrentUser({
      ...user,
      password: "",
      password_confirm: "",
      profile_id: user.profile?.id || user.profile_data?.id || null,
    });
    setIsEditing(true);
    setOpenModal(true);
    setFieldErrors({});
    setShowPassword(false);
    setShowPasswordConfirm(false);
  };

  const handleAdd = () => {
    setCurrentUser({
      username: "",
      email: "",
      first_name: "",
      last_name: "",
      password: "",
      password_confirm: "",
      is_active: true,
      profile_id: null,
    });
    setIsEditing(false);
    setOpenModal(true);
    setFieldErrors({});
    setShowPassword(false);
    setShowPasswordConfirm(false);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setFieldErrors({});
  };

  const handleCloseWithConfirmation = () => {
    if (window.confirm("Deseja realmente fechar? Todas as alterações não salvas serão descartadas.")) {
      setOpenModal(false);
    }
  };

  const handleSave = () => {
    const method = isEditing ? "PUT" : "POST";
    const url = isEditing
      ? `/backend/users/${currentUser.id}/`
      : "/backend/users/";

    // Prepara os dados para envio
    const payload = {
      username: currentUser.username,
      email: currentUser.email,
      first_name: currentUser.first_name,
      last_name: currentUser.last_name,
      is_active: currentUser.is_active,
      profile_id: currentUser.profile_id,
    };

    // Adiciona senha apenas se foi preenchida
    if (currentUser.password) {
      payload.password = currentUser.password;
      payload.password_confirm = currentUser.password_confirm;
    }

    fetch(url, {
      method: method,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    })
      .then(async (response) => {
        if (response.ok) return response.json();
        
        const errors = {};
        let generalError = null;
        
        try {
          const errorData = await response.json();
          
          const fieldMapping = {
            username: 'username',
            email: 'email',
            first_name: 'first_name',
            last_name: 'last_name',
            password: 'password',
            password_confirm: 'password_confirm',
            profile_id: 'profile_id',
          };
          
          Object.keys(fieldMapping).forEach(backendField => {
            if (errorData[backendField]) {
              if (Array.isArray(errorData[backendField])) {
                errors[fieldMapping[backendField]] = errorData[backendField][0];
              } else {
                errors[fieldMapping[backendField]] = errorData[backendField];
              }
            }
          });
          
          if (errorData.error) {
            generalError = errorData.error;
          } else if (errorData.non_field_errors && errorData.non_field_errors[0]) {
            generalError = errorData.non_field_errors[0];
          } else if (errorData.detail) {
            generalError = errorData.detail;
          }
          
          if (Object.keys(errors).length > 0) {
            setFieldErrors(errors);
          }
          
          if (generalError) {
            throw new Error(generalError);
          } else if (Object.keys(errors).length > 0) {
            throw new Error("Por favor, corrija os erros nos campos destacados.");
          } else {
            throw new Error("Não foi possível salvar o usuário. Verifique os dados e tente novamente.");
          }
        } catch (e) {
          if (e.message) {
            throw e;
          }
          console.warn("Não foi possível extrair mensagem de erro específica:", e);
          throw new Error("Não foi possível salvar o usuário. Verifique os dados e tente novamente.");
        }
      })
      .then((data) => {
        if (isEditing) {
          setUsers((prevUsers) =>
            prevUsers.map((user) => (user.id === data.id ? data : user))
          );
        } else {
          setUsers((prevUsers) => [...prevUsers, data]);
        }
        alert("Usuário salvo com sucesso!");
        setOpenModal(false);
        setFieldErrors({});
        fetchUsers();
      })
      .catch((error) => {
        console.error("Erro ao salvar usuário:", error);
        alert(error.message);
      });
  };

  const handleDelete = (userId) => {
    if (window.confirm("Deseja realmente excluir este usuário? Esta ação não pode ser desfeita.")) {
      fetch(`/backend/users/${userId}/`, {
        method: "DELETE",
      })
        .then(async (response) => {
          if (response.ok) {
            setUsers((prevUsers) =>
              prevUsers.filter((user) => user.id !== userId)
            );
            alert("Usuário removido com sucesso!");
            fetchUsers();
          } else {
            let errorMessage = "Não foi possível excluir o usuário.";
            try {
              const errorData = await response.json();
              if (errorData.error) {
                errorMessage = errorData.error;
              } else if (errorData.detail) {
                errorMessage = errorData.detail;
              }
            } catch (e) {
              console.warn("Não foi possível extrair mensagem de erro específica:", e);
            }
            throw new Error(errorMessage);
          }
        })
        .catch((error) => {
          console.error("Erro ao excluir usuário:", error);
          alert(error.message);
        });
    }
  };

  const getSelectedProfile = () => {
    return profiles.find(p => p.id === currentUser.profile_id) || null;
  };

  return (
    <div className="w-full overflow-auto">
      <MainCard
        title="Cadastro de Usuários"
        actions={[
          <AddButton onClick={handleAdd}>
            <IconPlus stroke={1.5} size="1.1rem" sx={{ mr: 5 }} />
            Adicionar usuário
          </AddButton>,
        ]}
      >
        <SearchSection onSearchChange={setSearchTerm} />
        <Divider sx={{ my: 2 }} />
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Usuário</TableCell>
              <TableCell>Nome</TableCell>
              <TableCell>Email</TableCell>
              <TableCell align="center">Perfil</TableCell>
              <TableCell align="center">Status</TableCell>
              <TableCell align="right">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users
              // Se não é superusuário, filtra para não mostrar superusuários
              .filter((user) => isSuperuser || !user.is_superuser)
              .map((user) => (
              <TableRow key={user.id}>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {/* Só mostra ícone de superusuário se o usuário logado for superusuário */}
                    {isSuperuser && (
                      user.is_superuser ? (
                        <Tooltip title="Superusuário">
                          <IconShield stroke={1.5} size="1.2rem" color="gold" />
                        </Tooltip>
                      ) : (
                        <IconShieldOff stroke={1.5} size="1.2rem" color="gray" />
                      )
                    )}
                    {user.username}
                  </Box>
                </TableCell>
                <TableCell>{user.full_name || `${user.first_name} ${user.last_name}`.trim() || "-"}</TableCell>
                <TableCell>{user.email || "-"}</TableCell>
                <TableCell align="center">
                  {user.profile_data ? (
                    <Chip 
                      label={user.profile_data.name} 
                      color="primary" 
                      size="small"
                      variant="outlined"
                    />
                  ) : (
                    <Chip 
                      label="Sem perfil" 
                      color="default" 
                      size="small"
                      variant="outlined"
                    />
                  )}
                </TableCell>
                <TableCell align="center">
                  <Chip 
                    label={user.is_active ? "Ativo" : "Inativo"} 
                    color={user.is_active ? "success" : "default"}
                    size="small"
                  />
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Editar">
                    <EditButton onClick={() => handleEdit(user)}>
                      <IconEdit stroke={1.5} size="1.3rem" />
                    </EditButton>
                  </Tooltip>
                  <Tooltip title="Excluir">
                    <DeleteButton 
                      onClick={() => handleDelete(user.id)}
                      disabled={user.is_superuser}
                    >
                      <IconTrash stroke={1.5} size="1.3rem" />
                    </DeleteButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </MainCard>

      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={totalRecords}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage="Usuários por página"
      />

      <Dialog 
        open={openModal} 
        onClose={(event, reason) => {
          if (reason === 'backdropClick') {
            return;
          }
          if (reason === 'escapeKeyDown') {
            handleCloseWithConfirmation();
            return;
          }
          handleCloseModal();
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {isEditing ? "Editar Usuário" : "Adicionar Usuário"}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            {/* Username */}
            <Grid item xs={12} sm={6}>
              <TextField
                label="Nome de Usuário"
                value={currentUser.username}
                onChange={(e) => {
                  setCurrentUser({ ...currentUser, username: e.target.value });
                  if (fieldErrors.username) {
                    setFieldErrors({ ...fieldErrors, username: null });
                  }
                }}
                fullWidth
                sx={{ mt: 2 }}
                autoComplete="off"
                error={!!fieldErrors.username}
                helperText={fieldErrors.username || ""}
                required
              />
            </Grid>

            {/* Email */}
            <Grid item xs={12} sm={6}>
              <TextField
                label="Email"
                type="email"
                value={currentUser.email}
                onChange={(e) => {
                  setCurrentUser({ ...currentUser, email: e.target.value });
                  if (fieldErrors.email) {
                    setFieldErrors({ ...fieldErrors, email: null });
                  }
                }}
                fullWidth
                sx={{ mt: 2 }}
                autoComplete="off"
                error={!!fieldErrors.email}
                helperText={fieldErrors.email || ""}
              />
            </Grid>

            {/* First Name */}
            <Grid item xs={12} sm={6}>
              <TextField
                label="Nome"
                value={currentUser.first_name}
                onChange={(e) => {
                  setCurrentUser({ ...currentUser, first_name: e.target.value });
                  if (fieldErrors.first_name) {
                    setFieldErrors({ ...fieldErrors, first_name: null });
                  }
                }}
                fullWidth
                autoComplete="off"
                error={!!fieldErrors.first_name}
                helperText={fieldErrors.first_name || ""}
              />
            </Grid>

            {/* Last Name */}
            <Grid item xs={12} sm={6}>
              <TextField
                label="Sobrenome"
                value={currentUser.last_name}
                onChange={(e) => {
                  setCurrentUser({ ...currentUser, last_name: e.target.value });
                  if (fieldErrors.last_name) {
                    setFieldErrors({ ...fieldErrors, last_name: null });
                  }
                }}
                fullWidth
                autoComplete="off"
                error={!!fieldErrors.last_name}
                helperText={fieldErrors.last_name || ""}
              />
            </Grid>

            {/* Password */}
            <Grid item xs={12} sm={6}>
              <TextField
                label={isEditing ? "Nova Senha (opcional)" : "Senha"}
                type={showPassword ? "text" : "password"}
                value={currentUser.password}
                onChange={(e) => {
                  setCurrentUser({ ...currentUser, password: e.target.value });
                  if (fieldErrors.password) {
                    setFieldErrors({ ...fieldErrors, password: null });
                  }
                }}
                fullWidth
                autoComplete="new-password"
                error={!!fieldErrors.password}
                helperText={fieldErrors.password || (isEditing ? "Deixe em branco para manter a senha atual" : "")}
                required={!isEditing}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <IconEyeOff size="1.2rem" /> : <IconEye size="1.2rem" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            {/* Password Confirm */}
            <Grid item xs={12} sm={6}>
              <TextField
                label="Confirmar Senha"
                type={showPasswordConfirm ? "text" : "password"}
                value={currentUser.password_confirm}
                onChange={(e) => {
                  setCurrentUser({ ...currentUser, password_confirm: e.target.value });
                  if (fieldErrors.password_confirm) {
                    setFieldErrors({ ...fieldErrors, password_confirm: null });
                  }
                }}
                fullWidth
                autoComplete="new-password"
                error={!!fieldErrors.password_confirm}
                helperText={fieldErrors.password_confirm || ""}
                required={!isEditing || !!currentUser.password}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPasswordConfirm(!showPasswordConfirm)}
                        edge="end"
                      >
                        {showPasswordConfirm ? <IconEyeOff size="1.2rem" /> : <IconEye size="1.2rem" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            {/* Perfil */}
            <Grid item xs={12}>
              <Autocomplete
                options={profiles}
                getOptionLabel={(option) => option.name || ""}
                value={getSelectedProfile()}
                onChange={(event, newValue) => {
                  setCurrentUser({ 
                    ...currentUser, 
                    profile_id: newValue ? newValue.id : null 
                  });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Perfil de Permissões"
                    error={!!fieldErrors.profile_id}
                    helperText={fieldErrors.profile_id || "Selecione o perfil de permissões do usuário"}
                  />
                )}
              />
            </Grid>

            {/* Status */}
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={currentUser.is_active}
                    onChange={(e) => setCurrentUser({ ...currentUser, is_active: e.target.checked })}
                    color="success"
                  />
                }
                label="Usuário ativo"
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCloseModal} color="secondary">
            Cancelar
          </Button>
          <Button onClick={handleSave} color="primary">
            Salvar
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default UsersList;

